<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model" :title="$t('Lot Up ')" @show="onShow"
                   @shown="onShown" :content-in-form="false" @hidden="onHidden"
                   :show-confirmation="showConfirmation" :ok-title="$t('Submit')" :is-loading="loading"
                   scrollable modal-class="mobile-full-screen">

      <div class="row">
        <div class="col-md-12">

          <div class="row mb-3">
            <div class="col-md-4">
              <receipt-typeahead ref="receipt" :model="model" attribute="receipt"
                                 :creation-source="SOURCE_LOT_UP_MODAL"/>
              <receipt-modal/>
            </div>
            <div class="col-md-4">
              <b-input-group>
                <b-form-input type="text"
                              :disabled="disableItemNoInput"
                              :placeholder="$t('Item Number')"
                              autocomplete="off"
                              v-model="itemNo"
                              @keydown.enter="searchInventoryItem"
                              @blur="searchInventoryItem"/>
                <b-input-group-append>
                  <b-button variant="primary" :disabled="disableItemNoInput" tabindex="-1">
                    <font-awesome-icon v-if="!itemNoLoading" icon="search" class="icon"/>
                    <b-spinner v-else small label="Spinning"></b-spinner>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>
          </div>

          <div class="row mt-3 mb-3" v-if="model.receipt && model.receipt.customer">
            <div class="col-md-12">
              <table class="table table-sm mb-0">
                <tr>
                  <td class="border-top-0 pl-0">
                    <span class="font-weight-bold">{{ $t('Customer information: ') }} </span>
                    <router-link tabindex="-1"
                                 :to="{ name: 'customers.dashboard', params: { id: model.receipt.customer.uuid }}">
                      {{ model.receipt.customer.display_name }}
                    </router-link>
                    <span v-if="model.receipt.customer && model.receipt.customer.billing_address"
                          v-html="$options.filters.asAddress(model.receipt.customer.billing_address, false)">
                      </span>
                  </td>
                </tr>
              </table>
            </div>
          </div>

          <div class="row mt-3 mb-3" v-if="errorMessage">
            <div class="col-md-12">
              <b-alert class="mb-0" variant="danger" show>
                {{ errorMessage }}
              </b-alert>
            </div>
          </div>

          <hr class="mt-0">
        </div>
      </div>

      <div class="row position-relative">
        <div class="position-absolute"
             v-if="isEntireFormDisabled || errorMessage"
             style="left: 0; right: 0; bottom: 0;top:0;background-color: rgba(236,241,247,0.5);z-index: 2">
        </div>

        <div class="col-xl-8 border-right">

          <b-alert variant="info" :show="!!itemNoMessage">
            {{ itemNoMessage }}
          </b-alert>

          <b-form @submit.prevent="handleSubmit(save)">
            <div class="row">
              <div class="col-md-4" v-if="!enableContinuousAuction">
                <input-widget :disabled="shouldDisableFields" :model="model" attribute="lot_no"/>
              </div>
              <div class="col-md-4" v-if="enableTimedSessions && modal.sale && modal.sale.type === 'timed'">
                <input-widget :model="model" attribute="auction_date_uuid"
                              :rules="model && model.lot_no ? 'required' : ''"
                              display-field="${item.title}" type="select2"
                              :items="modal.sale.dates"/>
              </div>
              <div class="col-md-4">
                <item-location-select-input-widget :disabled="shouldDisableFields"
                                                   :model="model" attribute="item_location_uuid"/>
              </div>
              <div class="col-md-4" v-if="model">
                <department-select-input-widget @select="onDepartmentSelect" :disabled="shouldDisableFields" @unselect="onDepartmentUnselect"
                                                :model="model" attribute="department_uuid"/>
              </div>
              <div class="col-md-4" v-if="model">
                <category-select-input-widget @select="onCategorySelect" get-dynamic-fields-template
                                              :model="model" :disabled="shouldDisableFields"
                                              :department="model.department" attribute="category_uuid"/>
              </div>
              <div class="col-md-4">
                <input-widget :model="model" attribute="quantity" v-if="enableInventoryQuantity"/>
              </div>
              <div class="col-md-12">
                <user-select-input-widget :model="model" attribute="specialists_uuids" multiple :items="model.specialists" :disabled="shouldDisableFields || !model.department_uuid"
                                          :filter-extra-data="specialistExtraFilters"/>
              </div>
              <div class="col-md-4" v-if="modal.sale && modal.sale.auction_collections && model.lot_no">
                <auction-collection-select-input-widget :model="model" attribute="auction_collection_uuid"
                                                        :items="modal.sale.auction_collections"/>
              </div>
            </div>
            <session-section v-if="enableContinuousAuction" :model="model"/>
            <translatable-input-widget v-if="!dynamicFieldsLoading" :disabled="shouldDisableFields" :hint="false"
                                       :model="model" :fields="model.sorted_dynamic_fields_template" :errors="errors"/>
            <div class="row">
              <div class="col-md-6">
                <input-widget :model="model" attribute="use_images_for_auction_gallery" type="checkbox"
                              :is-switch="true"
                              size="lg" @change="setCollectionInformation"
                              :disabled="shouldDisableFields"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_enabled" type="checkbox"
                              :is-switch="true"
                              size="lg" @change="setCollectionInformation"
                              :disabled="shouldDisableFields"/>
              </div>
            </div>
            <div class="row" v-if="model.collection_info_enabled">
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_address_1"
                              :disabled="shouldDisableFields"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_address_2"
                              :disabled="shouldDisableFields"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_state" :disabled="shouldDisableFields"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_city" :disabled="shouldDisableFields"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_zip_code" :disabled="shouldDisableFields"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_phone" :disabled="shouldDisableFields"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_email" :disabled="shouldDisableFields"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_notes" :disabled="shouldDisableFields"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_first_name" :disabled="shouldDisableFields"/>
              </div>
              <div class="col-md-6">
                <input-widget :model="model" attribute="collection_info_last_name" :disabled="shouldDisableFields"/>
              </div>
            </div>

            <!--            <div class="row">-->
            <!--              <div class="col-md-4">-->
            <!--                <currency-input-widget :disabled="shouldDisableFields" :currency="saleCurrency" :model="model"-->
            <!--                                       attribute="low"/>-->
            <!--              </div>-->
            <!--              <div class="col-md-4">-->
            <!--                <currency-input-widget :disabled="shouldDisableFields" :currency="saleCurrency" :model="model"-->
            <!--                                       attribute="high"/>-->
            <!--              </div>-->
            <!--              <div class="col-md-4">-->
            <!--                <currency-input-widget :disabled="shouldDisableFields" :currency="saleCurrency" :model="model"-->
            <!--                                       attribute="reserve"/>-->
            <!--              </div>-->
            <!--            </div>-->
            <!--            <div class="row">-->
            <!--              <div class="col-md-4">-->
            <!--                <currency-input-widget :disabled="shouldDisableFields" :currency="saleCurrency" :model="model"-->
            <!--                                       attribute="start_price"/>-->
            <!--              </div>-->
            <!--            </div>-->
          </b-form>
        </div>
        <div class="col-xl-4 mb-4">
          <images-view :hide-save-btn="true" :inventory-uuid="model.inventory_uuid"
                       :run-polling-on-save="false" ref="imageViewer" enable-image-edit/>
        </div>
      </div>

      <template v-slot:modal-footer>

        <template v-if="enableInventoryDraft">
          <cancel-button @click="hideModal" :disabled="loading">
            {{ $t('Cancel') }}
          </cancel-button>
          <submit-button @click="handleSubmit(onDraftSave)"
                         :disabled="loading || isEntireFormDisabled || shouldDisableFields" :show-loader="loading">
            {{ $t('Save as Draft') }}
          </submit-button>
          <submit-button @click="handleSubmit(save)" :disabled="loading || isEntireFormDisabled || shouldDisableFields"
                         :show-loader="loading">
            {{ $t('Save') }}
          </submit-button>
        </template>
        <template v-else>
          <cancel-button @click="hideModal" :disabled="loading">
            {{ $t('Cancel') }}
          </cancel-button>
          <submit-button @click="handleSubmit(save)"
                         :disabled="loading || isEntireFormDisabled || shouldDisableFields"
                         :show-loader="loading">
            {{ $t('Submit') }}
          </submit-button>
        </template>

      </template>

    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal.vue";
  import {createNamespacedHelpers} from "vuex";
  import LotModel from "../models/Lot.model";
  import CancelButton from "../../../core/components/common/CancelButton.vue";
  import SubmitButton from "../../../core/components/common/SubmitButton.vue";
  import InputWidget from "../../../core/components/input-widget/InputWidget.vue";
  import _ from 'lodash';
  import ReceiptTypeahead from "@/core/widgets/ReceiptTypeahead";
  import ImagesView from "@/modules/inventory/view/images/ImagesView";
  import InventoryService from "@/modules/inventory/InventoryService";
  import ItemLocationSelectInputWidget from "@/core/widgets/ItemLocationSelectInputWidget";
  import TranslatableInputWidget from "@/core/widgets/TranslatableInputWidget";
  import {SOURCE_LOT_UP_MODAL} from "../../constants";
  import ReceiptModal from "@/modules/receipt/ReceiptModal";
  import DepartmentSelectInputWidget from "@/core/widgets/DepartmentSelectInputWidget";
  import CategorySelectInputWidget from "@/core/widgets/CategorySelectInputWidget";
  import SessionSection from "../components/SessionSection";
  import UserSelectInputWidget from "@/core/widgets/UserSelectInputWidget.vue";
  import AuctionCollectionSelectInputWidget from "@/core/widgets/AuctionCollectionSelectInputWidget";
  import LotsService from "@/modules/lots/LotsService";
  import helperService from "@/core/services/helper.service";

  const {mapState, mapActions} = createNamespacedHelpers('lots');
  const {mapState: inventoryMapState} = createNamespacedHelpers('inventory');
  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');

  export default {
    name: "LotUpModal",
    components: {
      AuctionCollectionSelectInputWidget,
      UserSelectInputWidget,
      SessionSection,
      CategorySelectInputWidget,
      DepartmentSelectInputWidget,
      ReceiptModal,
      TranslatableInputWidget,
      ItemLocationSelectInputWidget,
      ImagesView,
      ReceiptTypeahead,
      InputWidget,
      SubmitButton,
      CancelButton,
      ArtisioModal
    },
    data() {
      return {
        itemNo: null,
        itemNoLoading: false,
        itemNoMessage: null,
        errorMessage: null,
        loading: false,
        showConfirmation: true,
        model: new LotModel(),
        saleCurrency: null,
        SOURCE_LOT_UP_MODAL: SOURCE_LOT_UP_MODAL,
        dynamicFieldsLoading: false,
      }
    },
    computed: {
      ...mapState({
        modal: state => state.modal,
        editObject: state => state.modal.object,
      }),
      ...inventoryMapState({
        images: state => state.view.images
      }),
      ...mapStateGlobal({
        isDynamicFieldsTemplateFromCategory: state => state.isDynamicFieldsTemplateFromCategory,
        enableInventoryQuantity: state => state.enableInventoryQuantity,
        enableContinuousAuction: state => state.enableContinuousAuction,
        enableInventoryDraft: state => state.enableInventoryDraft,
        enableTimedSessions: state => state.enableTimedSessions,
      }),
      isEntireFormDisabled() {
        return !this.model.receipt || !this.itemNo;
      },
      shouldDisableFields() {
        return this.model.status === 'sold' || this.model.status === 'unsold' || !!this.errorMessage;
      },
      disableItemNoInput() {
        return !this.model.receipt || !this.model.receipt.uuid || this.itemNoLoading;
      },
      specialistExtraFilters() {
        return {specialist_department_uuid: this.model && this.model.department_uuid ? this.model.department_uuid : null};
      }
    },
    methods: {
      ...mapActions(['hideModal', 'createLot']),
      onShow() {
        this.itemNo = null;
        this.itemNoMessage = null;
        this.errorMessage = null;
        this.showConfirmation = true;

        if (this.modal.sale && this.modal.sale.currency) {
          this.saleCurrency = this.modal.sale.currency.symbol;
        }
      },
      onShown() {
        this.$refs.receipt.focus()
      },
      onHidden() {
        this.model = new LotModel();
        this.hideModal();
      },
      onDepartmentSelect(department) {
        // this.dynamicFieldsLoading = true;
        this.model.department = department;
        this.model.category_uuid = null;
        this.model.specialists_uuids = [];
        this.model.specialists = [];
        // this.model.initDynamicFields(this.model, this.model.department.dynamic_fields_template)
        // this.$nextTick(() => {
        //   this.dynamicFieldsLoading = false;
        // })
      },
      onDepartmentUnselect() {
        this.model.specialists_uuids = [];
        this.model.specialists = [];
      },
      onCategorySelect(category) {
        this.dynamicFieldsLoading = true;
        this.model.category = category;
        this.model.category_uuid = category.uuid;
        this.model.initDynamicFields(this.model, this.model.category.dynamic_fields_template)
        this.$nextTick(() => {
          this.dynamicFieldsLoading = false;
        })
      },
      onDraftSave() {
        this.save(true)
      },
      async save(is_draft = false) {
        this.loading = true;

        const reservation_proposals = (await LotsService.getLatestReservationProposals({
          stock_nos: [this.model.stock_no],
          suggestions: ['return', 'transfer'],
        })).body;
        if (reservation_proposals.length) {
          const confirm = await helperService.showProposalsConfirmationModal(reservation_proposals, this.$confirm, this.$router, this.$options.filters)
          if (!confirm) {
            this.loading = false;
            return;
          }
        }

        if (!this.model.receipt_uuid) {
          this.model.receipt_uuid = this.model.receipt.uuid;
        }

        if (!this.model.item_no) {
          this.model.item_no = this.itemNo;
        }

        if (!this.model.auction_uuid) {
          this.model.auction_uuid = this.modal.sale.uuid;
        }

        this.model.assignToDynamicFields();

        let modelResponse = await this.createLot({...this.model.toJSON(), is_draft});

        if (modelResponse.success) {

          let continueLogic = true;

          if (this.images.length > 0) {
            // Returns boolean if images were save in backend
            continueLogic = await this.$refs.imageViewer.save(modelResponse.body.inventory_uuid);
          }

          this.loading = false;

          // Does not continue if image save return false, which means something went wrong
          if (continueLogic) {
            this.showConfirmation = false;
            this.hideModal();
            // this.resetForm();
            this.$toast('success', this.$t('Item was successfully created'));
          }
        } else {
          this.loading = false;
          if (modelResponse.toastError) {
            this.$errorDialog(modelResponse.toastError);
          } else {
            if (modelResponse.body.dates) {
              for (let index in modelResponse.body.dates) {
                this.model.dates[index].setMultipleErrors(modelResponse.body.dates[index]);
              }
            } else {
              this.model.setMultipleErrors(modelResponse.body);
            }
          }
        }
      },
      async searchInventoryItem() {
        this.itemNoMessage = null;
        this.errorMessage = null;
        this.$refs.imageViewer.clearImages();

        if (this.itemNo && this.model.receipt) {
          this.itemNoLoading = true;
          let {success, body} = await InventoryService.getInventories({
            item_no: this.itemNo,
            receipt_uuid: this.model.receipt.uuid,
            expand: 'department,category.dynamic_fields_template,available_quantity,stock_no,customer.shipping_address,account_managers,specialists'
          });
          this.itemNoLoading = false;

          if (success && body.results.length === 1) {
            let inventoryItem = body.results[0];
            if (inventoryItem.available_quantity === 0) {
              if (this.enableInventoryQuantity) {
                this.errorMessage = this.$i18n.t("Following item has insufficient quantity left in stock");
              } else {
                this.errorMessage = this.$i18n.t("Following item has already been reserved.");
              }
              this.model = new LotModel({receipt: this.model.receipt});
            } else {
              this.model = new LotModel({...this.model, ..._.cloneDeep(inventoryItem)});
            }
          } else {
            this.errorMessage = this.$i18n.t("Inventory item not found. Please try another item");
            this.model = new LotModel({receipt: this.model.receipt});
          }
        }

        setTimeout(() => {
          this.$refs.observer.reset();
        }, 300)
      },
      resetForm() {
        this.$refs.imageViewer.clearImages();
        this.model = new LotModel();
        this.$refs.receipt.clear();
        this.itemNo = null;
        this.itemNoMessage = null;
        this.errorMessage = false;
        this.$refs.observer.reset();
        setTimeout(() => {
          this.$refs.observer.reset();
        }, 300);
      },
      setCollectionInformation(collection_enabled) {
        if (!collection_enabled) {
          this.model.collection_info_address_1 = null;
          this.model.collection_info_address_2 = null;
          this.model.collection_info_state = null;
          this.model.collection_info_city = null;
          this.model.collection_info_zip_code = null;
          this.model.collection_info_phone = null;
          this.model.collection_info_first_name = null;
          this.model.collection_info_last_name = null;
          this.model.collection_info_email = null;
          this.model.collection_info_notes = null;
        } else {
          if (this.model.customer && this.model.customer.shipping_address) {
            this.model.collection_info_address_1 = this.model.collection_info_address_1 ? this.model.collection_info_address_1 : this.model.customer.shipping_address.address_1;
            this.model.collection_info_address_2 = this.model.collection_info_address_2 ? this.model.collection_info_address_2 : this.model.customer.shipping_address.address_2;
            this.model.collection_info_state = this.model.collection_info_state ? this.model.collection_info_state : this.model.customer.shipping_address.state;
            this.model.collection_info_city = this.model.collection_info_city ? this.model.collection_info_city : this.model.customer.shipping_address.city;
            this.model.collection_info_zip_code = this.model.collection_info_zip_code ? this.model.collection_info_zip_code : this.model.customer.shipping_address.zip_code;
            this.model.collection_info_first_name = this.model.collection_info_first_name ? this.model.collection_info_first_name : this.model.customer.shipping_address.first_name;
            this.model.collection_info_last_name = this.model.collection_info_last_name ? this.model.collection_info_last_name : this.model.customer.shipping_address.last_name;
            this.model.collection_info_phone = this.model.collection_info_phone ? this.model.collection_info_phone : this.model.customer.phone ? this.model.customer.phone.split(',')[0] : null;
            this.model.collection_info_email = this.model.collection_info_email ? this.model.collection_info_email : this.model.customer.email ? this.model.customer.email : null;
          }
        }
      }
    },
    watch: {
      editObject() {
        if (this.editObject && this.editObject.uuid) {
          this.model = new LotModel(this.editObject);
        }
      },
    },
  }
</script>

<style scoped>

</style>
